import ChartType from '@opsbase/lib-chart/ChartType';
import ChartVariant from '@opsbase/lib-chart/ChartVariant';
import Measure from '@opsbase/lib-chart/Measure';
import QueryFieldKey from '@opsbase/lib-data-queryfield/QueryFieldKey';
import DataType from '@opsbase/lib-datatype/DataType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import TimeInterval from '@opsbase/lib-time/TimeInterval';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions';
import useOrgUpdate from '@opsbase/react-gql-hooks/organisation/useOrgUpdate';
import AppPage from '@opsbase/web-app/AppPage';
import { maxWidgetsPerDashboard } from '@opsbase/web-app/consts';
import useViewRegistry from '@opsbase/web-ctrl-view/useViewRegistry';
import addChartToDashboard from '@opsbase/web-data-chart/helpers/addChartToDashboard';
import ConfirmDialog from '@opsbase/web-dialogs/ConfirmDialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import PrintChartIcon from '@opsbase/web-icons/icons/PrintChartIcon';
import Horizontal from '@opsbase/web-layout/Horizontal/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import MeasurePicker from '@opsbase/web-pickers/MeasurePicker';
import TimeIntervalPicker from '@opsbase/web-pickers/TimeIntervalPicker';
import Fab from '@opsbase/web-ui/Fab';
import MenuItem from '@opsbase/web-ui/MenuItem/MenuItem';
import RadioGroup from '@opsbase/web-ui/RadioGroup/RadioGroup';
import Select from '@opsbase/web-ui/Select';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useEffect, useState } from 'react';
import * as uuid from 'uuid';
import Dashboard from '../../shared/UX/Dashboard';
export function AnalyticsDashboardPage() {
    var _a, _b;
    const self = useSelf();
    const views = useViewRegistry();
    const { updateSettings } = useSelfActions();
    const { raiseError } = useNotificationActions();
    const updateOrg = useOrgUpdate();
    const [dashboardWidgets, setDashboardWidgets] = useState(uniqWidgets((_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.dashboardWidgets));
    const isEditable = self.isPrimary || self.isSuperUser;
    const [adding, setAdding] = useState(undefined);
    const { goTo } = useNav();
    useEffect(() => { var _a; return setDashboardWidgets(uniqWidgets((_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.dashboardWidgets)); }, [self]);
    const handleUpdateDashboardWidgets = useCallback(v => {
        const newDashboardWidgets = uniqWidgets(v);
        setDashboardWidgets(newDashboardWidgets);
        updateOrg({
            id: self.organisationId,
            patch: { settings: { dashboardWidgets: newDashboardWidgets } },
        })
            .then(updatedOrg => {
            updateSettings(updatedOrg === null || updatedOrg === void 0 ? void 0 : updatedOrg.settings);
        })
            .catch(doNothing);
    }, [self, updateSettings, updateOrg]);
    const handleExportDashboardPDF = useCallback(() => {
        goTo(Pages.DASHBOARD_EXPORT);
    }, [goTo]);
    const handleOpenDashboardWidget = useCallback(viewId => {
        const lmss = views[viewId];
        if (!lmss)
            return;
        const page = lmss.type === DataType.OP ? Pages.OPS_LOG : Pages.ISSUES_LOG;
        goTo(page, { viewId });
    }, [goTo, views]);
    const handleStartAdding = useCallback(() => {
        if (dashboardWidgets.length >= maxWidgetsPerDashboard)
            return raiseError(i18n.t('dashboard-full'));
        setAdding({
            key: uuid.v4(),
            chartType: ChartType.TABLE3D,
            chartVariant: ChartVariant.PERF_GROUP_TIME,
            config: {
                measure: Measure.PUNCTUALITY,
                dimension: QueryFieldKey.PLACE,
                interval: TimeInterval.WEEK,
            },
        });
    }, [dashboardWidgets, raiseError]);
    const handleChangeAdding = useCallback(k => v => setAdding(set({ ...adding }, k, v)), [adding]);
    const handleCancelAdding = useCallback(() => setAdding(undefined), []);
    const handleConfirmAdding = useCallback(async () => {
        var _a;
        handleUpdateDashboardWidgets(addChartToDashboard((_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.dashboardWidgets, undefined, adding));
    }, [adding, handleUpdateDashboardWidgets, (_b = self.settings) === null || _b === void 0 ? void 0 : _b.dashboardWidgets]);
    return (React.createElement(AppPage, null,
        React.createElement(Dashboard, { isEditable: isEditable, dashboardWidgets: dashboardWidgets, onOpen: handleOpenDashboardWidget, onChange: handleUpdateDashboardWidgets }),
        React.createElement(Horizontal, { gutter: true },
            isEditable && React.createElement(Fab, { onClick: handleStartAdding }),
            isEditable && (React.createElement(Fab, { onClick: handleExportDashboardPDF, icon: React.createElement(PrintChartIcon, null), fabStyle: { right: 76 } }))),
        React.createElement(ConfirmDialog, { size: DialogSize.X450_Y350, open: !!adding, color: Palette.secondary, onClose: handleCancelAdding, onConfirm: handleConfirmAdding, title: i18n.t('add-to-dashboard') }, !!adding && (React.createElement(Vertical, { gutter: true, padding: true, centered: true },
            React.createElement(RadioGroup, { columns: 2, value: adding.chartType, onChange: handleChangeAdding('chartType'), options: [
                    { value: ChartType.TABLE3D, label: i18n.t('table') },
                    { value: ChartType.LINE3DGROUP, label: i18n.t('line') },
                ] }),
            React.createElement(MeasurePicker, { noMargin: true, value: adding.config.measure, onChange: handleChangeAdding('config.measure') }),
            React.createElement(Horizontal, { shrink: true, gutter: true },
                React.createElement(PerfGroupPicker, { noMargin: true, value: adding.config.dimension, onChange: handleChangeAdding('config.dimension') }),
                React.createElement(TimeIntervalPicker, { noMargin: true, value: adding.config.interval, onChange: handleChangeAdding('config.interval') })),
            React.createElement(Pane, { grow: true }))))));
}
export default AnalyticsDashboardPage;
function PerfGroupPicker(props) {
    return (React.createElement(Select, { ...props },
        React.createElement(MenuItem, { value: QueryFieldKey.PLACE }, dimensionToStr(QueryFieldKey.PLACE)),
        React.createElement(MenuItem, { value: QueryFieldKey.OPDEFINITION }, dimensionToStr(QueryFieldKey.OPDEFINITION))));
}
function dimensionToStr(v) {
    switch (v) {
        case QueryFieldKey.PLACE:
            return i18n.t('place');
        case QueryFieldKey.OPDEFINITION:
            return i18n.t('operation');
    }
}
function uniqWidgets(w) {
    return uniqBy(w, v => v.key);
}
